import React, { useState } from "react";
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    TableSortLabel,
    useTheme,
    useMediaQuery
} from "@mui/material";
import { UserWithRole } from "../../../../../models/User";
import { UsersService } from "../../../../../services/supabase/UsersService";
import { UserRow } from "./UserRow";
import { TableControlBar } from "../../../../../components/TableControlBar";
import { PortalTable } from "../../../../../components/PortalTable";
import { tablePreferencesStore } from "../../../../../app/stores/TablePreferences";

interface UsersTableProps {
    users: UserWithRole[];
}

export const UsersTable: React.FC<UsersTableProps> = ({ users }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { getAllUsers } = UsersService();
    
    // Get preferences from store
    const { 
        users: usersPrefs, 
        updateUsersPreferences 
    } = tablePreferencesStore();
    
    // Use store values with local state for page and search (not persisted)
    const [order, setOrder] = useState<'asc' | 'desc'>(usersPrefs.sortOrder);
    const [orderBy, setOrderBy] = useState<keyof UserWithRole>(usersPrefs.sortBy as keyof UserWithRole);
    const [page, setPage] = useState(0); // Session-only
    const [rowsPerPage, setRowsPerPage] = useState<number>(usersPrefs.rowsPerPage);
    const [dense, setDense] = useState(usersPrefs.dense);
    const [isLoading, setIsLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState(''); // Session-only

    // Filter users by search query
    const filteredUsers = users.filter(user => {
        if (!searchQuery.trim()) return true;
        const query = searchQuery.toLowerCase();
        return (
            user.firstName?.toLowerCase().includes(query) ||
            user.lastName?.toLowerCase().includes(query) ||
            user.email?.toLowerCase().includes(query) ||
            user.roleName?.toLowerCase().includes(query) ||
            user.mobileNumber?.toLowerCase().includes(query)
        );
    });

    const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - filteredUsers.length) : 0;

    const sortedUsers = filteredUsers.slice().sort((a, b) => {
        if (a[orderBy] === null && b[orderBy] === null) {
            return 0;
        } else if (a[orderBy] === null) {
            return 1;
        } else if (b[orderBy] === null) {
            return -1;
        }

        if (orderBy === 'firstName' || orderBy === 'lastName' || orderBy === 'email' || orderBy === 'roleName' || orderBy === 'created' || orderBy === 'updated') {
            return (
                order === 'asc' ?
                    a[orderBy].localeCompare(b[orderBy])
                    :
                    b[orderBy].localeCompare(a[orderBy])
            );
        } else {
            return (
                order === 'asc' ?
                    (a[orderBy] as any) - (b[orderBy] as any)
                    :
                    (b[orderBy] as any) - (a[orderBy] as any)
            );
        }
    });

    const handleRequestSort = (event: React.MouseEvent<unknown>, property: keyof UserWithRole) => {
        const isAsc = orderBy === property && order === 'asc';
        const newOrder = isAsc ? 'desc' : 'asc';
        setOrder(newOrder);
        setOrderBy(property);
        
        // Persist to store
        updateUsersPreferences({
            sortOrder: newOrder,
            sortBy: property as string
        });
    };

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newRowsPerPage = +event.target.value;
        setRowsPerPage(newRowsPerPage);
        setPage(0);
        
        // Persist to store
        updateUsersPreferences({ rowsPerPage: newRowsPerPage });
    };

    const handleRefresh = async () => {
        setIsLoading(true);
        await getAllUsers();
        setIsLoading(false);
    };

    return (
        <Box
            sx={{
                width: '100%',
                mb: 4,
            }}
        >
            {/* Controls Bar */}
            <TableControlBar
                dense={dense}
                onDenseChange={(newDense) => {
                    setDense(newDense);
                    updateUsersPreferences({ dense: newDense });
                }}
                isLoading={isLoading}
                onRefresh={handleRefresh}
                search={{
                    value: searchQuery,
                    onChange: setSearchQuery,
                    placeholder: 'Search users by name, email, role...'
                }}
                filterDescription={
                    searchQuery
                        ? `Showing ${filteredUsers.length} of ${users.length} users matching "${searchQuery}"`
                        : `Showing all ${users.length} users`
                }
            />

            <PortalTable>
                <TableContainer>
                    <Table stickyHeader size={dense ? 'small' : 'medium'}>
                        <TableHead>
                            <TableRow>
                                <TableCell>
                                    <TableSortLabel
                                        sx={{ ml: 1, fontFamily: "primaryFont", fontWeight: "bold", letterSpacing: 1 }}
                                        active={orderBy === 'firstName'}
                                        direction={orderBy === 'firstName' ? order : 'asc'}
                                        onClick={(event) => handleRequestSort(event, 'firstName')}
                                    >
                                        User
                                    </TableSortLabel>
                                </TableCell>
                                {/* Email - hidden on mobile, shown in User column */}
                                <TableCell align="left" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                                    <TableSortLabel
                                        sx={{ fontFamily: "primaryFont", fontWeight: "bold", letterSpacing: 1 }}
                                        active={orderBy === 'email'}
                                        direction={orderBy === 'email' ? order : 'asc'}
                                        onClick={(event) => handleRequestSort(event, 'email')}
                                    >
                                        Email
                                    </TableSortLabel>
                                </TableCell>
                                {/* Role + Actions combined on mobile */}
                                <TableCell align={isMobile ? 'right' : 'left'}>
                                    <TableSortLabel
                                        sx={{ fontFamily: "primaryFont", fontWeight: "bold", letterSpacing: 1 }}
                                        active={orderBy === 'roleName'}
                                        direction={orderBy === 'roleName' ? order : 'asc'}
                                        onClick={(event) => handleRequestSort(event, 'roleName')}
                                    >
                                        {isMobile ? 'Role & Actions' : 'Role'}
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell align="left" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                                    <TableSortLabel
                                        sx={{ fontFamily: "primaryFont", fontWeight: "bold", letterSpacing: 1 }}
                                        active={orderBy === 'mobileNumber'}
                                        direction={orderBy === 'mobileNumber' ? order : 'asc'}
                                        onClick={(event) => handleRequestSort(event, 'mobileNumber')}
                                    >
                                        Phone
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell align="left" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                                    <TableSortLabel
                                        sx={{ fontFamily: "primaryFont", fontWeight: "bold", letterSpacing: 1 }}
                                        active={orderBy === 'created'}
                                        direction={orderBy === 'created' ? order : 'asc'}
                                        onClick={(event) => handleRequestSort(event, 'created')}
                                    >
                                        Created
                                    </TableSortLabel>
                                </TableCell>
                                {/* Actions - hidden on mobile, combined with Role */}
                                <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                                    Actions
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sortedUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((user) => (
                                <UserRow
                                    key={user.id}
                                    user={user}
                                />
                            ))}
                            {emptyRows > 0 && (
                                <TableRow style={{ height: (dense ? 33 : 53) * emptyRows }}>
                                    <TableCell colSpan={6} />
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    component="div"
                    count={filteredUsers.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </PortalTable>
        </Box>
    );
};
